import { useState, useRef, useEffect, useCallback } from "react";
import api from "@/utils/api";
import {
  Wifi, Copy, CheckCircle2, Loader2, X, ArrowRight, ArrowLeft, Camera,
  Plus, Trash2,
} from "lucide-react";

/**
 * DeviceOnboardWizard — guided Wi-Fi onboarding for an AMeru counter device.
 *
 * Step 1  Identify   scan the device QR (or type its ID) + map DI channels.
 * Step 2  Connect    hand the plant Wi-Fi to the device via SoftAP provisioning
 *                    (the device opens a temporary "AMeru-Setup-xxxx" network;
 *                     the operator's tablet passes SSID+password, authenticated
 *                     by the provisioning code shown here).
 * Step 3  Confirm    poll until the device reports in, then mark it active.
 *
 * The device is registered with status "onboarding"; the first counter it pushes
 * flips it to "active" (server-side), which this wizard detects as "online".
 */
const softApName = (deviceId) => {
  const tail = (deviceId || "").replace(/[^a-z0-9]/gi, "").slice(-6) || "xxxxxx";
  return `AMeru-Setup-${tail}`;
};

const DeviceOnboardWizard = ({ machines = [], onClose, onDone }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    device_id: "", pop: "",
    mappings: [{ channel: "1", machine_code: "", rfid_tag_uid: "" }],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [status, setStatus] = useState(null); // { online, status, last_absolute }
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const scanTimer = useRef(null);
  const pollTimer = useRef(null);

  const canScan = typeof window !== "undefined" && "BarcodeDetector" in window;

  // ── QR scan (progressive enhancement; manual entry always works) ──
  const stopScan = useCallback(() => {
    setScanning(false);
    if (scanTimer.current) { clearInterval(scanTimer.current); scanTimer.current = null; }
    const v = videoRef.current;
    if (v && v.srcObject) { v.srcObject.getTracks().forEach((t) => t.stop()); v.srcObject = null; }
  }, []);

  const applyScanned = (text) => {
    let device_id = "", pop = "";
    try {
      const obj = JSON.parse(text);
      device_id = obj.device_id || obj.id || "";
      pop = obj.pop || obj.provisioning_code || "";
    } catch {
      const parts = String(text).split(/[|:;,]/).map((s) => s.trim());
      device_id = parts[0] || "";
      pop = parts[1] || "";
    }
    if (device_id) setForm((f) => ({ ...f, device_id, ...(pop ? { pop } : {}) }));
  };

  const startScan = async () => {
    setErr(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      setScanning(true);
      const v = videoRef.current;
      if (!v) return;
      v.srcObject = stream;
      await v.play();
      const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
      scanTimer.current = setInterval(async () => {
        try {
          const codes = await detector.detect(v);
          if (codes && codes.length) {
            applyScanned(codes[0].rawValue);
            stopScan();
          }
        } catch { /* frame not ready */ }
      }, 400);
    } catch (e) {
      setErr("Camera unavailable — type the device ID instead.");
      setScanning(false);
    }
  };

  useEffect(() => () => { stopScan(); if (pollTimer.current) clearInterval(pollTimer.current); }, [stopScan]);

  // ── Step 1 → register as "onboarding" ──
  const updateMapping = (index, key, value) => {
    setForm((f) => ({
      ...f,
      mappings: f.mappings.map((m, i) => (i === index ? { ...m, [key]: value } : m)),
    }));
  };

  const addMapping = () => {
    setForm((f) => {
      const used = new Set(f.mappings.map((m) => parseInt(m.channel)));
      const next = Array.from({ length: 8 }, (_, i) => i + 1).find((ch) => !used.has(ch));
      return next ? {
        ...f,
        mappings: [...f.mappings, { channel: String(next), machine_code: "", rfid_tag_uid: "" }],
      } : f;
    });
  };

  const removeMapping = (index) => {
    setForm((f) => ({ ...f, mappings: f.mappings.filter((_, i) => i !== index) }));
  };

  const startOnboarding = async () => {
    const mappings = form.mappings.map((m) => ({
      channel: parseInt(m.channel),
      machine_code: (m.machine_code || "").trim(),
      rfid_tag_uid: (m.rfid_tag_uid || "").trim(),
    }));
    // A DI channel may be reserved on the device without a machine yet — leave
    // machine_code blank to onboard first, assign later.
    if (!form.device_id.trim() || !mappings.length) {
      setErr("Device ID and at least one DI channel row are required");
      return;
    }
    setBusy(true); setErr(null);
    try {
      await api.post("/iot/devices", {
        device_id: form.device_id.trim(),
        mappings,
        machine_code: (mappings.find((m) => m.machine_code) || mappings[0]).machine_code,
        channel: mappings[0].channel || 1,
        pop: form.pop || "",
        status: "onboarding",
      });
      setStep(2);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not register the device");
    }
    setBusy(false);
  };

  // ── Step 3 → poll status until online ──
  const beginPolling = () => {
    setStep(3);
    const tick = async () => {
      try {
        const { data } = await api.get(`/iot/onboard/status/${encodeURIComponent(form.device_id.trim())}`);
        setStatus(data);
        if (data.online || data.status === "active") {
          if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
        }
      } catch { /* keep polling */ }
    };
    tick();
    pollTimer.current = setInterval(tick, 4000);
  };

  const finish = () => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    onDone && onDone();
    onClose && onClose();
  };

  const copy = (t) => { try { navigator.clipboard.writeText(t); } catch { /* noop */ } };

  const online = status?.online || status?.status === "active";
  const ssid = softApName(form.device_id);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <Wifi size={18} /> Onboard Device <span className="text-slate-400">/ डिवाइस जोड़ें</span>
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2 px-6 pt-4">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex-1 flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
                ${step >= n ? "bg-[#1A3560] text-white" : "bg-slate-100 text-slate-400"}`}>{n}</div>
              {n < 3 && <div className={`flex-1 h-0.5 ${step > n ? "bg-[#1A3560]" : "bg-slate-100"}`} />}
            </div>
          ))}
        </div>
        <div className="px-6 pb-1 pt-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
          {step === 1 ? "1 · Identify" : step === 2 ? "2 · Connect to Wi-Fi" : "3 · Confirm"}
        </div>

        {err && (
          <div className="mx-6 mt-2 px-4 py-2 rounded-lg text-sm font-bold bg-red-100 text-red-700">{err}</div>
        )}

        {/* Body */}
        <div className="p-6 pt-3">
          {step === 1 && (
            <div className="space-y-4">
              {scanning ? (
                <div className="space-y-2">
                  <video ref={videoRef} className="w-full rounded-xl bg-black aspect-video object-cover" muted playsInline />
                  <button onClick={stopScan}
                    className="w-full h-12 rounded-lg border border-slate-300 text-slate-600 font-bold text-sm">
                    Cancel scan
                  </button>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1">Device ID</label>
                    <div className="flex gap-2">
                      <input value={form.device_id}
                        onChange={(e) => setForm((f) => ({ ...f, device_id: e.target.value }))}
                        placeholder="ws-s3eth-5f3aa32a"
                        className="flex-1 h-12 px-3 border border-slate-300 rounded-lg text-sm font-mono" />
                      {canScan && (
                        <button onClick={startScan}
                          className="h-12 px-4 rounded-lg border border-slate-300 text-slate-600 font-bold text-xs inline-flex items-center gap-1">
                          <Camera size={16} /> Scan
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Scan the QR on the device label, or type its ID.</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
                        DI Channel → Machine
                      </label>
                      <button onClick={addMapping} disabled={form.mappings.length >= 8}
                        className="text-xs font-bold text-[#1A3560] disabled:text-slate-300 inline-flex items-center gap-1">
                        <Plus size={13} /> Add channel
                      </button>
                    </div>
                    <div className="space-y-2">
                      {form.mappings.map((mapping, index) => (
                        <div key={`${index}-${mapping.channel}`} className="grid grid-cols-[64px_1fr_1fr_28px] gap-2 items-center">
                          <select value={mapping.channel}
                            onChange={(e) => updateMapping(index, "channel", e.target.value)}
                            className="h-11 px-2 border border-slate-300 rounded-lg text-sm">
                            {Array.from({ length: 8 }, (_, i) => i + 1).map((ch) => (
                              <option key={ch} value={ch}>DI{ch}</option>
                            ))}
                          </select>
                          <select value={mapping.machine_code}
                            onChange={(e) => updateMapping(index, "machine_code", e.target.value)}
                            className="h-11 px-2 border border-slate-300 rounded-lg text-sm">
                            <option value="">— Unassigned —</option>
                            {machines.map((m) => (
                              <option key={m.machine_code} value={m.machine_code}>{m.machine_code}</option>
                            ))}
                          </select>
                          <input value={mapping.rfid_tag_uid || ""}
                            onChange={(e) => updateMapping(index, "rfid_tag_uid", e.target.value)}
                            placeholder="RFID UID (future)"
                            className="h-11 px-2 border border-slate-300 rounded-lg text-xs font-mono" />
                          <button onClick={() => removeMapping(index)} disabled={form.mappings.length === 1}
                            className="text-red-500 disabled:text-slate-300" title="Remove mapping">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1">
                      Provisioning code <span className="font-normal normal-case tracking-normal text-slate-400">(optional)</span>
                    </label>
                    <input value={form.pop}
                      onChange={(e) => setForm((f) => ({ ...f, pop: e.target.value }))}
                      placeholder="from device label"
                      className="w-full h-12 px-3 border border-slate-300 rounded-lg text-sm font-mono" />
                  </div>
                </>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Power on the device. It opens a temporary Wi-Fi network for a few minutes so you can
                hand it your plant Wi-Fi.
              </p>
              <ol className="space-y-3 text-sm">
                <li className="flex gap-3">
                  <span className="w-6 h-6 shrink-0 rounded-full bg-slate-100 text-slate-600 text-xs font-bold flex items-center justify-center">1</span>
                  <span>On this tablet's Wi-Fi settings, connect to
                    <span className="font-mono font-bold text-slate-900"> {ssid}</span>.</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 shrink-0 rounded-full bg-slate-100 text-slate-600 text-xs font-bold flex items-center justify-center">2</span>
                  <span>Open the <b>AMeru Onboard</b> app (ESP SoftAP Provisioning), enter the code below,
                    then choose your plant Wi-Fi and type its password.</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 shrink-0 rounded-full bg-slate-100 text-slate-600 text-xs font-bold flex items-center justify-center">3</span>
                  <span>The device joins your Wi-Fi and comes online. Reconnect this tablet to plant Wi-Fi.</span>
                </li>
              </ol>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1">Provisioning code</div>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-mono text-lg font-extrabold text-slate-900 break-all">
                    {form.pop || "— (printed on the device label)"}
                  </div>
                  {form.pop && (
                    <button onClick={() => copy(form.pop)} className="text-slate-500 hover:text-slate-800">
                      <Copy size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="py-4 text-center">
              {online ? (
                <div className="space-y-2">
                  <CheckCircle2 size={48} className="mx-auto text-green-600" />
                  <div className="text-lg font-extrabold text-slate-900">Device is online</div>
                  <div className="text-sm text-slate-500">
                    {form.device_id} · {status?.mappings?.length || form.mappings.length} machine mapping(s)
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <Loader2 size={40} className="mx-auto text-[#1A3560] animate-spin" />
                  <div className="text-sm font-bold text-slate-700">Waiting for the device to report in…</div>
                  <div className="text-xs text-slate-400">
                    Registered as <b>onboarding</b>. This flips to <b>active</b> automatically on the first stroke.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between gap-2 px-6 py-4 border-t border-slate-200">
          <button onClick={step === 1 ? onClose : () => setStep(step - 1)}
            disabled={step === 3 && !online}
            className="px-4 h-12 text-sm font-bold rounded-lg border border-slate-300 text-slate-600 inline-flex items-center gap-1 disabled:opacity-40">
            <ArrowLeft size={16} /> {step === 1 ? "Cancel" : "Back"}
          </button>
          {step === 1 && (
            <button onClick={startOnboarding} disabled={busy}
              className="px-5 h-12 text-sm font-bold rounded-lg bg-[#1A3560] text-white inline-flex items-center gap-1 disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} Start onboarding
            </button>
          )}
          {step === 2 && (
            <button onClick={beginPolling}
              className="px-5 h-12 text-sm font-bold rounded-lg bg-[#1A3560] text-white inline-flex items-center gap-1">
              I've done this — check status <ArrowRight size={16} />
            </button>
          )}
          {step === 3 && (
            <button onClick={finish} disabled={!online}
              className="px-5 h-12 text-sm font-bold rounded-lg bg-green-700 text-white inline-flex items-center gap-1 disabled:opacity-40">
              <CheckCircle2 size={16} /> Finish
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeviceOnboardWizard;
